import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useAuth } from '../context/AuthContext';
import { api, formatMYR, formatDate, formatDateTime, calculateProgress, daysRemaining, parseMYRToCents, openFileUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { ShareButtons } from '../components/campaign/ShareButtons';
import { toast } from 'sonner';
import {
  Calendar,
  Users,
  ArrowLeft,
  Settings,
  QrCode,
  Building2,
  CreditCard,
  FileText,
  MessageSquare,
  Megaphone,
  Send,
  User,
  Upload,
  Loader2,
  Receipt,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const statusColors = {
  PENDING_REVIEW: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
  ENDED: 'bg-gray-100 text-gray-800 border-gray-200',
};

const statusLabels = {
  PENDING_REVIEW: 'Pending Review',
  APPROVED: 'Active',
  REJECTED: 'Rejected',
  ENDED: 'Ended',
};

const proofStatusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [comments, setComments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [myDonations, setMyDonations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Donation proof state
  const [donationAmount, setDonationAmount] = useState('');
  const [donationMessage, setDonationMessage] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [submittingProof, setSubmittingProof] = useState(false);

  const isOwner = campaign?.owner_id === user?.id;
  const progress = campaign ? calculateProgress(campaign.raised_cents, campaign.target_cents) : 0;
  const spentProgress = campaign && campaign.raised_cents > 0 
    ? Math.min(100, Math.round((campaign.spent_cents / campaign.raised_cents) * 100)) 
    : 0;
  const days = campaign ? daysRemaining(campaign.deadline_date) : 0;
  const isActive = campaign?.status === 'APPROVED' && !campaign?.ended_at && days > 0;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const promises = [
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/comments`),
        api.get(`/campaigns/${id}/receipts`),
      ];
      if (user) {
        promises.push(api.get(`/campaigns/${id}/my-donations`));
      }
      const results = await Promise.all(promises);
      setCampaign(results[0].data);
      setComments(results[1].data);
      setReceipts(results[2].data);
      if (user && results[3]) {
        setMyDonations(results[3].data);
      } else {
        setMyDonations(null);
      }
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/discover');
    } finally {
      setLoading(false);
    }
  };

  const promptLogin = () => {
    const next = `/campaigns/${id}`;
    toast.info('Please sign in to continue');
    navigate(`/login?next=${encodeURIComponent(next)}`);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) { promptLogin(); return; }
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await api.post(`/campaigns/${id}/comments`, {
        message: newComment,
        is_owner_update: isOwner,
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success(isOwner ? 'Update posted!' : 'Comment posted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a JPG, PNG, or PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File must be less than 10MB');
        return;
      }
      setProofFile(file);
    }
  };

  const handleSubmitDonationProof = async (e) => {
    e.preventDefault();
    if (!user) { promptLogin(); return; }
    
    if (!donationAmount || parseFloat(donationAmount) <= 0) {
      toast.error('Please enter a valid donation amount');
      return;
    }
    if (!proofFile) {
      toast.error('Please upload proof of payment');
      return;
    }

    setSubmittingProof(true);
    try {
      const formData = new FormData();
      formData.append('amount_cents', parseMYRToCents(donationAmount).toString());
      formData.append('proof_file', proofFile);
      if (donationMessage) {
        formData.append('message', donationMessage);
      }

      await api.post(`/campaigns/${id}/donation-proofs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Donation proof submitted! The campaign owner will verify it.');
      setDonationAmount('');
      setDonationMessage('');
      setProofFile(null);
      
      // Refresh donations
      const donationsRes = await api.get(`/campaigns/${id}/my-donations`);
      setMyDonations(donationsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit donation proof');
    } finally {
      setSubmittingProof(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </PageLayout>
    );
  }

  if (!campaign) {
    return null;
  }

  const defaultImage = 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=800&q=80';
  const totalSpent = campaign.spent_cents || 0;

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Link
          to="/discover"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to campaigns
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Campaign Image */}
            <div className="relative rounded-2xl overflow-hidden aspect-[16/9]">
              <img
                src={campaign.image_url || defaultImage}
                alt={campaign.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = defaultImage;
                }}
              />
              <Badge
                className={`absolute top-4 left-4 ${statusColors[campaign.status]} border`}
              >
                {statusLabels[campaign.status]}
              </Badge>
            </div>

            {/* Tax Deductible Badge */}
            {campaign.is_tax_deductible && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Receipt className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-emerald-800">Tax Deductible Campaign</h4>
                        <Badge className="bg-emerald-200 text-emerald-800 text-xs">Verified Organization</Badge>
                      </div>
                      <p className="text-sm text-emerald-700 mb-2">
                        Donations to this campaign may be tax deductible under Section 44(6) of the Income Tax Act 1967.
                      </p>
                      <div className="text-sm space-y-1">
                        <p><span className="font-medium">Organization:</span> {campaign.tax_exempt_org_name}</p>
                        <p><span className="font-medium">Registration No:</span> {campaign.tax_exempt_org_number}</p>
                      </div>
                      <p className="text-xs text-emerald-600 mt-2">
                        * Please verify with LHDN or your tax advisor. Fundfair does not guarantee tax-exempt status.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Title and Owner Actions */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {campaign.title}
                </h1>
                <p className="text-muted-foreground">
                  by {campaign.owner_username || 'Anonymous'}
                </p>
              </div>
              {isOwner && (
                <Link to={`/campaigns/${id}/manage`}>
                  <Button variant="outline" className="gap-2 rounded-full" data-testid="manage-campaign-btn">
                    <Settings className="w-4 h-4" />
                    Manage
                  </Button>
                </Link>
              )}
            </div>

            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <p className="text-foreground whitespace-pre-wrap leading-relaxed">
                  {campaign.description}
                </p>
              </CardContent>
            </Card>

            {/* Social Share Buttons */}
            {campaign.status === 'APPROVED' && (
              <Card>
                <CardContent className="p-6">
                  <ShareButtons 
                    campaign={campaign} 
                    campaignUrl={window.location.href}
                  />
                </CardContent>
              </Card>
            )}

            {/* Payment Info & Donation Proof Upload */}
            {isActive && !isOwner && (campaign.payment_qr_url || campaign.bank_name) && (
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Make a Donation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Payment Methods */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {campaign.payment_qr_url && (
                      <div className="flex flex-col items-center gap-3 p-4 bg-white rounded-xl">
                        <QrCode className="w-6 h-6 text-muted-foreground" />
                        <img
                          src={campaign.payment_qr_url}
                          alt="Payment QR Code"
                          className="max-w-[180px] rounded-lg border"
                        />
                        <p className="text-sm text-muted-foreground">Scan to pay</p>
                      </div>
                    )}
                    
                    {campaign.bank_name && (
                      <div className="space-y-3 p-4 bg-white rounded-xl">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-primary" />
                          <span className="font-medium">Bank Transfer</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Bank:</span> {campaign.bank_name}</p>
                          {campaign.bank_account_number && (
                            <p><span className="text-muted-foreground">Account:</span> <span className="font-mono">{campaign.bank_account_number}</span></p>
                          )}
                          {campaign.account_holder_name && (
                            <p><span className="text-muted-foreground">Name:</span> {campaign.account_holder_name}</p>
                          )}
                        </div>
                        {campaign.payment_instructions && (
                          <p className="text-xs text-muted-foreground mt-2">{campaign.payment_instructions}</p>
                        )}
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Upload Proof of Payment (or sign-in CTA for guests) */}
                  {!user ? (
                    <div className="rounded-xl border border-primary/30 bg-white p-6 text-center">
                      <h4 className="font-semibold text-foreground mb-2 flex items-center justify-center gap-2">
                        <Upload className="w-4 h-4" />
                        Ready to donate?
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Sign in or create a free account to upload your payment proof and have your donation tracked on this campaign.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        <Button
                          onClick={() => navigate(`/login?next=${encodeURIComponent(`/campaigns/${id}`)}`)}
                          className="rounded-full"
                          data-testid="guest-donate-login-btn"
                        >
                          Log in to donate
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/signup?next=${encodeURIComponent(`/campaigns/${id}`)}`)}
                          className="rounded-full"
                          data-testid="guest-donate-signup-btn"
                        >
                          Create account
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Upload Proof of Payment
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        After making your payment, upload the proof below. The campaign owner will verify and confirm your donation.
                      </p>
                    
                    <form onSubmit={handleSubmitDonationProof} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Amount Donated (MYR) *</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">MYR</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={donationAmount}
                              onChange={(e) => setDonationAmount(e.target.value)}
                              placeholder="0.00"
                              className="pl-12 rounded-xl"
                              data-testid="donation-amount-input"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Proof of Payment *</Label>
                          <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary/50 transition-colors">
                            <Upload className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {proofFile ? proofFile.name : 'Upload screenshot/receipt'}
                            </span>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/jpg,application/pdf"
                              onChange={handleProofFileChange}
                              className="hidden"
                              data-testid="proof-file-input"
                            />
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Message (Optional)</Label>
                        <Textarea
                          value={donationMessage}
                          onChange={(e) => setDonationMessage(e.target.value)}
                          placeholder="Leave a message for the campaign owner..."
                          className="rounded-xl"
                          rows={2}
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={submittingProof}
                        className="w-full rounded-full"
                        data-testid="submit-proof-btn"
                      >
                        {submittingProof ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Donation Proof'
                        )}
                      </Button>
                    </form>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* My Donations */}
            {!isOwner && myDonations && myDonations.proofs && myDonations.proofs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Receipt className="w-5 h-5" />
                    My Donations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myDonations.proofs.map((proof) => (
                      <div key={proof.id} className="p-3 bg-secondary/50 rounded-xl space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className={proofStatusColors[proof.status]}>
                              {proof.status === 'PENDING' && <Clock className="w-3 h-3 mr-1" />}
                              {proof.status === 'VERIFIED' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {proof.status === 'REJECTED' && <XCircle className="w-3 h-3 mr-1" />}
                              {proof.status}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {formatDateTime(proof.created_at)}
                            </span>
                            <button
                              onClick={() => openFileUrl(proof.file_url, `my_proof_${proof.id}`)}
                              className="text-xs text-primary hover:underline"
                            >
                              View Proof
                            </button>
                          </div>
                          <span className="font-semibold">{formatMYR(proof.amount_cents)}</span>
                        </div>
                        
                        {/* Tax Receipt Download - only show for verified donations with tax receipts */}
                        {proof.status === 'VERIFIED' && proof.tax_receipt_url && (
                          <div className="flex items-center justify-between pt-2 border-t border-border/50">
                            <div className="flex items-center gap-2 text-sm text-emerald-700">
                              <Receipt className="w-4 h-4" />
                              <span>Tax receipt available</span>
                            </div>
                            <button
                              onClick={() => openFileUrl(proof.tax_receipt_url, `tax_receipt_${proof.id}`)}
                              className="text-sm text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1 bg-emerald-100 px-3 py-1 rounded-full"
                            >
                              <FileText className="w-4 h-4" />
                              Download Tax Receipt
                            </button>
                          </div>
                        )}
                        
                        {/* Pending tax receipt notice */}
                        {proof.status === 'VERIFIED' && !proof.tax_receipt_url && campaign.is_tax_deductible && (
                          <div className="flex items-center gap-2 pt-2 border-t border-border/50 text-sm text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            <span>Tax receipt pending from campaign owner</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t flex justify-between text-sm">
                    <span className="text-muted-foreground">Verified Total:</span>
                    <span className="font-semibold text-primary">{formatMYR(myDonations.total_verified_cents)}</span>
                  </div>
                  {myDonations.total_pending_cents > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-muted-foreground">Pending:</span>
                      <span className="text-yellow-600">{formatMYR(myDonations.total_pending_cents)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Comments & Updates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MessageSquare className="w-5 h-5" />
                  Comments & Updates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {(campaign.status === 'APPROVED' || isOwner) && (
                  user ? (
                    <form onSubmit={handleSubmitComment} className="space-y-3">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder={isOwner ? "Post an update for your supporters..." : "Leave a supportive message..."}
                        className="rounded-xl resize-none"
                        rows={3}
                        data-testid="comment-input"
                      />
                      <Button
                        type="submit"
                        disabled={submittingComment || !newComment.trim()}
                        className="rounded-full gap-2"
                        data-testid="submit-comment-btn"
                      >
                        <Send className="w-4 h-4" />
                        {submittingComment ? 'Posting...' : isOwner ? 'Post Update' : 'Post Comment'}
                      </Button>
                    </form>
                  ) : (
                    <div className="rounded-xl border border-border/60 bg-secondary/40 p-4 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-sm text-muted-foreground">
                        Want to leave a message of support? Sign in to join the conversation.
                      </p>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/login?next=${encodeURIComponent(`/campaigns/${id}`)}`)}
                        className="rounded-full"
                        data-testid="guest-comment-login-btn"
                      >
                        Sign in to comment
                      </Button>
                    </div>
                  )
                )}

                <Separator />

                <div className="space-y-4" data-testid="comments-list">
                  {comments.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No comments yet. Be the first to show support!
                    </p>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        className={`p-4 rounded-xl ${
                          comment.is_owner_update
                            ? 'bg-primary/10 border border-primary/20'
                            : 'bg-secondary/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {comment.is_owner_update ? (
                            <Badge className="bg-primary text-primary-foreground">
                              <Megaphone className="w-3 h-3 mr-1" />
                              Owner Update
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              {comment.commenter_username || 'Supporter'}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-foreground whitespace-pre-wrap">
                          {comment.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Receipt Proofs */}
            {receipts.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5" />
                    Spending Receipts ({receipts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3" data-testid="receipts-grid">
                    {receipts.map((receipt) => (
                      <button
                        key={receipt.id}
                        onClick={() => openFileUrl(receipt.file_url, `receipt_${receipt.id}`)}
                        className="w-full flex items-center justify-between p-3 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{receipt.caption || 'Receipt'}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(receipt.created_at)}</p>
                          </div>
                        </div>
                        <span className="font-semibold text-red-600">-{formatMYR(receipt.amount_cents)}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Card */}
            <Card className="sticky top-24">
              <CardContent className="p-6 space-y-6">
                {/* Amount Collected */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    Amount Collected
                  </div>
                  <div className="flex items-end justify-between">
                    <span className="text-3xl font-bold text-primary">
                      {formatMYR(campaign.raised_cents)}
                    </span>
                    <span className="text-muted-foreground">
                      of {formatMYR(campaign.target_cents)}
                    </span>
                  </div>
                  <Progress value={progress} className="h-3" />
                  <p className="text-sm text-muted-foreground">
                    {progress}% of goal reached
                  </p>
                </div>

                {/* Amount Spent */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    Amount Spent
                  </div>
                  <div className="flex items-end justify-between">
                    <span className="text-2xl font-bold text-red-600">
                      {formatMYR(totalSpent)}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      of {formatMYR(campaign.raised_cents)} raised
                    </span>
                  </div>
                  <Progress value={spentProgress} className="h-2 [&>div]:bg-red-500" />
                  <p className="text-sm text-muted-foreground">
                    {campaign.raised_cents > 0 
                      ? `${spentProgress}% of funds used` 
                      : 'No funds raised yet'}
                  </p>
                </div>

                <Separator />

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-secondary/50 rounded-xl text-center">
                    <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
                    <p className="text-2xl font-bold">{campaign.donation_count}</p>
                    <p className="text-sm text-muted-foreground">Donors</p>
                  </div>
                  <div className="p-4 bg-secondary/50 rounded-xl text-center">
                    <Calendar className="w-6 h-6 mx-auto mb-2 text-primary" />
                    <p className="text-2xl font-bold">{days}</p>
                    <p className="text-sm text-muted-foreground">Days Left</p>
                  </div>
                </div>

                {/* Deadline */}
                <div className="text-sm text-muted-foreground">
                  <p>
                    <strong>Deadline:</strong> {formatDate(campaign.deadline_date)}
                  </p>
                  <p>
                    <strong>Created:</strong> {formatDate(campaign.created_at)}
                  </p>
                </div>

                {!isActive && (
                  <div className="p-4 bg-secondary/50 rounded-xl text-center">
                    <p className="text-muted-foreground">
                      {campaign.status === 'ENDED' || campaign.ended_at
                        ? 'This campaign has ended'
                        : campaign.status === 'PENDING_REVIEW'
                        ? 'This campaign is pending review'
                        : campaign.status === 'REJECTED'
                        ? 'This campaign was not approved'
                        : 'Campaign deadline has passed'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
